import { useState, useCallback, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../hooks/useTheme';
import { api } from '../utils/api';
import { AdvancedRackCanvas } from '../components/equipment/AdvancedRackCanvas';
import { CompactRackHeader } from '../components/equipment/CompactRackHeader';
import { RackEditModal } from '../components/equipment/RackEditModal';
import { RackConnectivityView } from '../components/equipment/RackConnectivityView';
import { EquipmentFormDrawer } from '../components/equipment/EquipmentFormDrawer';
import { AICatalogPanel } from '../components/equipment/AICatalogPanel';
import { VisualCableTrace } from '../components/connectivity/VisualCableTrace';
import { RackPassportCard } from '../components/equipment/RackPassportCard';
import { RackSummaryPanel } from '../components/equipment/RackSummaryPanel';
import { RackPortsTab } from '../components/equipment/RackPortsTab';
import { RackPowerTab } from '../components/equipment/RackPowerTab';
import { DcMiniMap } from '../components/equipment/DcMiniMap';
import { LivingTwinOverlay } from '../components/equipment/LivingTwinOverlay';
import { RackReservationModal } from '../components/equipment/RackReservationModal';
import { useRackWorkspaceData, RackEquipmentItem } from '../hooks/useRackWorkspaceData';
import { useRackReservations } from '../hooks/useRackReservations';
import { useTelemetryStream } from '../hooks/useTelemetryStream';
import toast from 'react-hot-toast';
import { Search, Cable, Cpu, Zap, Server } from 'lucide-react';
import { TYPE_COLORS, TYPE_NAMES_RU } from '../components/canvas/equipmentTypes';
import type { CatalogEntry } from '../utils/equipmentApi';
import { useSystemSettings } from '../hooks/useSystemSettings';
import { useQuery } from '@tanstack/react-query';
import { usePatchingStore } from '../stores/usePatchingStore';
import { PatchingWorkbench } from '../components/equipment/PatchingWorkbench';
import { useRackValidation } from '../hooks/useRackValidation';
import { useEquipmentMutations } from '../hooks/useEquipmentMutations';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { getUniqueTenantsFromEquipment, filterEquipmentByTenant } from '../utils/tenancyCascade.logic';
import { parseReturnRackInfo } from '../utils/cableTraceFarEnd.logic';
import { clampLeftTab, getGhostPduFormDefaults } from '../utils/rackZeroU.logic';

export function RackWorkspacePage() {
  const { dcCode, code, rackId, id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { activeWorkspaceId } = useWorkspace();

  const actualCode = dcCode || code || '';
  const actualRackId = rackId || id;

  const { returnRackId, returnRackName, highlightPortId } = useMemo(
    () => parseReturnRackInfo(searchParams),
    [searchParams]
  );

  const { theme } = useTheme();
  const sysSettings = useSystemSettings();

  const [selectedEq, setSelectedEq] = useState<RackEquipmentItem | null>(null);
  const [showRackEditModal, setShowRackEditModal] = useState(false);
  const [isMountingMode, setIsMountingMode] = useState(false);
  
  const [projectionMode, setProjectionMode] = useState<'front' | 'rear' | 'split'>('split');
  const [activeMode, setActiveMode] = useState<'inspect' | 'thermal' | 'cable'>('inspect');
  const [leftTab, setLeftTab] = useState<'passport' | 'device' | 'ports' | 'power' | 'catalog' | 'cabling'>('passport');
  
  const liveMetrics = useTelemetryStream(actualCode, actualRackId ? parseInt(actualRackId, 10) : undefined);

  // PDU outlet summary — refetch every 60s
  const { data: powerSummary } = useQuery({
    queryKey: ['powerSummary', actualCode, actualRackId, activeWorkspaceId],
    queryFn: () =>
      api.get(`/api/datacenters/${actualCode}/racks/${actualRackId}/power-summary`, {
        params: { workspace: activeWorkspaceId }
      }).then(r => r.data),
    enabled: !!(actualCode && actualRackId),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  // Build map: equipment_id -> {free_outlets, total_outlets}
  const pduSummaries: Record<string, { free_outlets: number; total_outlets: number }> = {};
  if (powerSummary?.pdus) {
    for (const pdu of powerSummary.pdus) {
      if (pdu.equipment_id) {
        pduSummaries[String(pdu.equipment_id)] = {
          free_outlets: pdu.free_outlets ?? 0,
          total_outlets: pdu.total_outlets ?? 0,
        };
      }
    }
  }

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<RackEquipmentItem | null>(null);
  const [tracePortId, setTracePortId] = useState<string | null>(null);
  const [formInitialData, setFormInitialData] = useState<any>(null);

  // Hook handles data fetching and mutations
  const {
    racksList,
    equipmentList,
    dcInfo,
    isLoading,
    currentRack,
    slots,
    prevRack,
    nextRack,
    queryClient
  } = useRackWorkspaceData(actualCode, actualRackId, activeWorkspaceId);

  const [showReservationModal, setShowReservationModal] = useState(false);
  const [selectedTenantFilter, setSelectedTenantFilter] = useState<string>('all');
  const uniqueTenants = useMemo(
    () => getUniqueTenantsFromEquipment(equipmentList, currentRack?.tenant),
    [equipmentList, currentRack?.tenant]
  );
  const {
    reservations,
    spaceMetrics,
    addReservation,
    removeReservation,
  } = useRackReservations(actualRackId, slots, equipmentList);

  useEffect(() => {
    setSelectedEq(null);
  }, [actualRackId]);

  useEffect(() => {
    setLeftTab(prev => clampLeftTab(prev, selectedEq, isMountingMode) as any);
  }, [selectedEq, isMountingMode, actualRackId]);

  useEffect(() => {
    if (!code && actualRackId) {
      api.get(`/api/datacenters/racks/${actualRackId}`)
        .then(res => {
          if (res.data.datacenter_code) {
            navigate(`/${res.data.datacenter_code}/racks/${actualRackId}`, { replace: true });
          }
        })
        .catch(() => toast.error('Failed to resolve rack datacenter location'));
    }
  }, [code, actualRackId, navigate]);

  const [catalogEntries, setCatalogEntries] = useState<CatalogEntry[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);

  const handleCatalogSearch = useCallback(async (params: { manufacturer?: string; equipment_type?: string; query?: string }) => {
    setCatalogLoading(true);
    try {
      const res = await api.get('/api/equipment/catalog', { params });
      setCatalogEntries(res.data);
    } catch (err: any) {
      if (err?.response?.status !== 403) toast.error('Каталог оборудования недоступен');
    } finally {
      setCatalogLoading(false);
    }
  }, []);

  useEffect(() => {
    handleCatalogSearch({});
  }, [handleCatalogSearch]);

  const { data: selectedEqPorts = [] } = useQuery<any[]>({
    queryKey: ['equipmentPorts', selectedEq?.id],
    queryFn: () => api.get(`/api/equipment/${selectedEq?.id}/ports`).then(res => res.data),
    enabled: !!selectedEq?.id,
  });

  useEffect(() => {
    if (!highlightPortId || !actualCode || equipmentList.length === 0) return;

    api.get(`/api/datacenters/${actualCode}/connectivity/ports/${highlightPortId}`)
      .then(res => {
        const devId = res.data?.device_id || res.data?.equipment_id;
        if (devId) {
          const match = equipmentList.find(eq => String(eq.id) === String(devId));
          if (match) {
            setSelectedEq(match);
            setLeftTab('ports');
          }
        }
      })
      .catch(() => {
        // Silently ignore if port info cannot be resolved
      });
  }, [highlightPortId, actualCode, equipmentList]);

  const handleNavigateRack = (rId: string | number) => {
    navigate(`/${actualCode}/racks/${rId}`);
  };

  const handleSelectEquipment = (eq: RackEquipmentItem) => {
    const state = usePatchingStore.getState();
    const isCablingActive = state.isPatchingMode || leftTab === 'cabling';

    if (isCablingActive) {
      if (!state.isPatchingMode) {
        state.setPatchingMode(true);
      }
      const strId = String(eq.id);
      if (!state.leftEquipmentId) {
        state.setLeftEquipment(strId);
        toast.success(`Источник (Source): ${eq.name}`);
      } else if (!state.rightEquipmentId && String(state.leftEquipmentId) !== strId) {
        state.setRightEquipment(strId);
        toast.success(`Приёмник (Destination): ${eq.name}`);
      } else if (String(state.leftEquipmentId) === strId) {
        toast.error("Нельзя выбрать одно и то же устройство источником и приёмником");
      } else {
        // Override destination
        state.setRightEquipment(strId);
        toast.success(`Приёмник (Destination) изменен на: ${eq.name}`);
      }
    } else {
      setSelectedEq(eq);
      setLeftTab('device');
    }
  };

  const {
    handleValidate,
    freeUBlocks,
    usedU,
    fillPct,
    currentWeight,
    currentPower
  } = useRackValidation({
    currentRack,
    equipmentList,
    editingItem,
    slots,
    reservations
  });

  const {
    isSaving,
    handleEquipmentMove,
    handleFormSubmit,
    handleAISuggest,
    handleCatalogSelect,
    handleCatalogSearchForForm
  } = useEquipmentMutations({
    actualCode,
    actualRackId,
    workspace: activeWorkspaceId,
    editingItem,
    setEditingItem,
    setIsFormOpen,
    setFormInitialData,
    setSelectedEq
  });



  const handleOpenAddForm = () => {
    setFormInitialData({ name: '', equipment_type: 'server', u_height: 1, u_position: 1, mounting_position: 'Front', is_zero_u: false, weight_kg: 10, power_w: 0, status: 'Active', attributes: '{}' });
    setEditingItem(null);
    setIsFormOpen(true);
  };

  const handleSlotClick = (u: number, mount: string) => {
    const isZeroU = u === 0 || mount === 'Left' || mount === 'Right';
    if (isZeroU) {
      setFormInitialData(getGhostPduFormDefaults(mount as 'Left' | 'Right', equipmentList));
    } else {
      setFormInitialData({
        name: '',
        equipment_type: 'server',
        u_height: 1,
        u_position: u,
        mounting_position: mount,
        is_zero_u: false,
        weight_kg: 10,
        power_w: 0,
        status: 'Active',
        attributes: '{}',
      });
    }
    setEditingItem(null);
    setIsFormOpen(true);
  };



  if (!actualRackId) {
    return (
      <div className="h-screen flex flex-col items-center justify-center gap-3" style={{ backgroundColor: '#020617', color: '#f1f5f9' }}>
        <Server className="w-10 h-10 text-slate-600" />
        <p className="text-slate-400 text-sm">Стойка не найдена</p>
        <button onClick={() => navigate(`/${actualCode}`)} className="px-4 py-2 bg-cyan-700 hover:bg-cyan-600 text-white rounded-lg text-sm font-bold transition">
          ← Вернуться к датацентру
        </button>
      </div>
    );
  }

  return (
    <div className="h-screen w-full flex flex-col font-sans overflow-hidden select-none" style={{ backgroundColor: theme === 'light' ? '#f1f5f9' : '#020617', color: theme === 'light' ? '#0f172a' : '#f1f5f9' }}>
      {isLoading && (
        <div className="absolute inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: theme === 'light' ? 'rgba(241,245,249,0.85)' : 'rgba(2,6,23,0.85)' }}>
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
            <p className="text-xs text-slate-400 font-mono">Загрузка стойки...</p>
          </div>
        </div>
      )}

      <CompactRackHeader
        datacenterCode={actualCode} datacenterName={dcInfo?.name} rack={currentRack} equipmentList={equipmentList}
        racksList={racksList} spaceMetrics={spaceMetrics}
        projectionMode={projectionMode} setProjectionMode={setProjectionMode} isMountingMode={isMountingMode} setIsMountingMode={setIsMountingMode}
        onBack={() => navigate(-1)} onEditRack={() => setShowRackEditModal(true)} theme={theme}
        prevRack={prevRack} nextRack={nextRack} onNavigateRack={handleNavigateRack}
        onProvision2NPdus={() => setLeftTab('power')}
      />

      {returnRackId && (
        <div className="bg-cyan-950/90 border-y border-cyan-700/60 px-4 py-1.5 flex items-center justify-between text-xs font-mono text-cyan-200 backdrop-blur-sm z-20 shrink-0">
          <div className="flex items-center gap-2">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-500"></span>
            </span>
            <span>
              Переход по кабельной линии (Far-End Jump). Исходная стойка:{' '}
              <strong className="text-white">{returnRackName || `Стойка #${returnRackId}`}</strong>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                navigate(`/${actualCode}/racks/${returnRackId}`);
              }}
              className="inline-flex items-center gap-1 px-3 py-1 rounded bg-cyan-700 hover:bg-cyan-600 active:bg-cyan-800 text-white font-semibold transition-colors cursor-pointer"
            >
              <span>← Вернуться к стойке {returnRackName || returnRackId}</span>
            </button>
            <button
              type="button"
              onClick={() => {
                const next = new URLSearchParams(searchParams);
                next.delete('returnRack');
                next.delete('returnRackName');
                setSearchParams(next, { replace: true });
              }}
              className="text-cyan-400 hover:text-white px-1.5 py-0.5 cursor-pointer text-sm"
              title="Скрыть плашку возврата"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      <div className="flex-1 flex gap-2 p-1.5 overflow-hidden min-h-0 h-[calc(100vh-42px)]">
        {/* ── Left inspector panel ── */}
        <div
          className="w-[38%] h-full flex flex-col overflow-hidden min-h-0"
          style={{
            background: 'var(--bg-panel)',
            border: '1px solid var(--border-color)',
            borderRadius: 2,
            boxShadow: '0 4px 32px rgba(0,0,0,0.35)',
          }}
        >
          {/* Tab bar */}
          <div
            className="flex shrink-0 gap-0.5 p-1"
            style={{
              background: 'var(--bg-app)',
              borderBottom: '1px solid var(--border-color)',
            }}
            role="tablist"
            aria-label="Панели инспектора"
          >
            {([
              {
                id: 'passport',
                icon: Server,
                label: 'Стойка',
                title: 'Паспорт стойки',
                always: true,
              },
              {
                id: 'device',
                icon: Cpu,
                label: selectedEq ? selectedEq.name : 'Прибор',
                title: selectedEq ? `Прибор: ${selectedEq.name}` : 'Паспорт оборудования',
                always: false,
                show: !!selectedEq,
              },
              {
                id: 'ports',
                icon: Zap,
                label: 'Интерфейсы',
                title: 'Интерфейсы и порты прибора',
                always: false,
                show: !!selectedEq,
              },
              {
                id: 'catalog',
                icon: Search,
                label: 'Каталог',
                title: 'Каталог оборудования',
                always: false,
                show: isMountingMode,
              },
              {
                id: 'power',
                icon: Zap,
                label: 'Питание',
                title: 'Распределение питания и PDU',
                always: true,
              },
              {
                id: 'cabling',
                icon: Cable,
                label: 'Коммутация',
                title: 'Кабельные трассы и коммутация',
                always: true,
              },
            ] as const)
              .filter(t => t.always || (t as any).show)
              .map(tab => {
                const isActive = leftTab === tab.id;
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    role="tab"
                    aria-selected={isActive}
                    aria-controls={`panel-${tab.id}`}
                    title={tab.title}
                    onClick={() => { setLeftTab(tab.id as any); setTracePortId(null); }}
                    className="flex-1 flex items-center justify-center gap-1 py-1 px-1 text-[10px] font-semibold rounded-sm transition-colors focus-visible:outline-none focus-visible:ring-1 min-w-0"
                    style={{
                      background: isActive ? 'var(--bg-active)' : 'transparent',
                      color: isActive ? 'var(--accent)' : 'var(--text-muted)',
                      border: isActive ? '1px solid var(--border-focus)' : '1px solid transparent',
                    }}
                  >
                    <Icon size={11} className="shrink-0" aria-hidden />
                    <span className="truncate">{tab.label}</span>
                  </button>
                );
              })
            }
          </div>

          {/* Tab content */}
          <div
            id={`panel-${leftTab}`}
            role="tabpanel"
            className="flex-1 overflow-y-auto p-3 text-xs"
            style={{ fontFamily: 'var(--font-ui)' }}
          >
            {leftTab === 'passport' && (
              <RackSummaryPanel
                currentRack={currentRack}
                equipmentList={equipmentList}
                racksList={racksList}
                dcInfoName={dcInfo?.name}
                actualCode={actualCode}
                actualRackId={actualRackId}
                theme={theme}
                sysSettings={sysSettings}
                slots={slots}
                fillPct={fillPct}
                usedU={usedU}
                currentWeight={currentWeight}
                currentPower={currentPower}
                freeUBlocks={freeUBlocks}
                onAddEquipment={handleOpenAddForm}
                reservations={reservations}
                spaceMetrics={spaceMetrics}
                onAddReservation={() => setShowReservationModal(true)}
                onReleaseReservation={(res) => removeReservation(res.id)}
                onSelectEquipment={handleSelectEquipment}
                onSlotClick={handleSlotClick}
                onSelectRack={(rId) => handleNavigateRack(rId)}
                DcMiniMap={DcMiniMap}
                selectedEqId={selectedEq?.id}
                liveMetrics={liveMetrics}
              />
            )}
            {leftTab === 'device' && selectedEq && (
              <RackPassportCard
                selectedEq={selectedEq}
                theme={theme}
                actualCode={actualCode}
                actualRackId={actualRackId}
                liveMetrics={liveMetrics?.[selectedEq.id]}
                onBack={() => {
                  setSelectedEq(null);
                  setLeftTab('passport');
                }}
                onEdit={(eq) => {
                  setEditingItem(eq as any);
                  setFormInitialData({ ...eq });
                  setIsFormOpen(true);
                }}
              />
            )}
            {leftTab === 'ports' && selectedEq && (
              <RackPortsTab
                selectedEq={selectedEq}
                ports={selectedEqPorts}
                onTrace={(portId) => setTracePortId(String(portId))}
                highlightPortId={highlightPortId}
                onPortStatusChange={() => {
                  queryClient.invalidateQueries({ queryKey: ['equipmentPorts', selectedEq?.id] });
                }}
              />
            )}
            {leftTab === 'power' && (
              <RackPowerTab
                equipmentList={equipmentList}
                maxPower={currentRack?.max_power_w || 5000}
                powerSummary={powerSummary}
                datacenterCode={actualCode}
                rackId={actualRackId}
                workspace={activeWorkspaceId}
                onProvisionSuccess={() => {
                  queryClient.invalidateQueries({ queryKey: ['powerSummary'] });
                  queryClient.invalidateQueries({ queryKey: ['equipment'] });
                  queryClient.invalidateQueries({ queryKey: ['rackConfig'] });
                  queryClient.invalidateQueries({ queryKey: ['racks'] });
                }}
              />
            )}
            {leftTab === 'catalog' && <AICatalogPanel catalogEntries={catalogEntries} isLoading={catalogLoading} onCatalogSelect={(e) => handleCatalogSelect(e, freeUBlocks, slots)} onAISuggest={handleAISuggest} onSearch={handleCatalogSearch} />}
            {leftTab === 'cabling' && <RackConnectivityView equipmentList={equipmentList} dcCode={actualCode} rackId={actualRackId || ''} onTracePort={(portId) => setTracePortId(portId)} />}
          </div>
        </div>

        {/* ── Right canvas panel ── */}
        <div
          className="w-[62%] h-full flex flex-col overflow-y-auto min-h-0 relative"
          style={{
            background: theme === 'light' ? '#f8fafc' : '#020617',
            border: '1px solid var(--border-color)',
            borderRadius: 2,
          }}
        >
          <LivingTwinOverlay rackId={actualRackId || ''} />

          <div
            className="shrink-0 flex items-center gap-1 px-2 py-1.5"
            style={{
              background: 'var(--bg-app)',
              borderBottom: '1px solid var(--border-color)',
              fontFamily: 'var(--font-data)',
              fontSize: 11,
            }}
          >
            <button
              onClick={() => setActiveMode('inspect')}
              className="px-3 py-1 text-xs font-semibold rounded-sm transition-colors"
              style={{
                background: activeMode === 'inspect' ? 'var(--accent)' : 'transparent',
                color: activeMode === 'inspect' ? '#fff' : 'var(--text-muted)',
                border: '1px solid ' + (activeMode === 'inspect' ? 'var(--accent)' : 'transparent'),
              }}
            >
              ⌕ Просмотр
            </button>
            <button
              onClick={() => setActiveMode('thermal')}
              className="px-3 py-1 text-xs font-semibold rounded-sm transition-colors"
              style={{
                background: activeMode === 'thermal' ? 'var(--status-warning)' : 'transparent',
                color: activeMode === 'thermal' ? '#000' : 'var(--text-muted)',
                border: '1px solid ' + (activeMode === 'thermal' ? 'var(--status-warning)' : 'transparent'),
              }}
            >
              🌡 Тепловая карта
            </button>

            {/* Hallmark Segmented / Select Tenant Filter */}
            {uniqueTenants.length > 1 && (
              <div className="flex items-center gap-1.5 ml-2 border-l pl-2" style={{ borderColor: 'var(--border-color)' }}>
                <span className="text-[10px] font-mono" style={{ color: 'var(--text-muted)' }}>Владелец:</span>
                <select
                  data-testid="rack-tenant-filter-select"
                  value={selectedTenantFilter}
                  onChange={(e) => setSelectedTenantFilter(e.target.value)}
                  className="text-xs rounded px-1.5 py-0.5 font-mono focus:outline-none transition-colors"
                  style={{
                    backgroundColor: 'var(--bg-input)',
                    border: '1px solid var(--border-color)',
                    color: 'var(--text-primary)',
                  }}
                >
                  <option value="all">Все ({equipmentList.length})</option>
                  {uniqueTenants.map(t => {
                    const count = filterEquipmentByTenant(equipmentList, t, currentRack?.tenant).length;
                    return (
                      <option key={t} value={t}>
                        {t} ({count})
                      </option>
                    );
                  })}
                </select>
              </div>
            )}

            <div className="ml-auto flex items-center gap-3 pr-1" style={{ fontSize: 9, color: 'var(--text-muted)' }}>
              {(['server','switch','router','patch_panel','pdu','ups','firewall','storage'] as const).map(t => (
                <span key={t} className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-sm" style={{ backgroundColor: TYPE_COLORS[t] }} aria-hidden />
                  {TYPE_NAMES_RU[t] ?? t}
                </span>
              ))}
            </div>
          </div>

          <AdvancedRackCanvas
            uSlots={slots}
            equipmentList={equipmentList}
            projectionMode={projectionMode}
            activeFilter="all"
            activeMode={activeMode}
            isMountingMode={isMountingMode}
            onEquipmentClick={handleSelectEquipment}
            onSlotClick={(u, mount) => handleSlotClick(u, mount)}
            onEquipmentMove={(id, u, mount) => handleEquipmentMove(id, u, mount)}
            theme={theme}
            showEmptySlots={sysSettings.show_empty_slots}
            liveMetrics={liveMetrics}
            pduSummaries={pduSummaries}
            reservations={reservations}
            onReleaseReservation={(res) => removeReservation(res.id)}
            rackTenant={currentRack?.tenant}
            activeTenantFilter={selectedTenantFilter}
          />
        </div>
      </div>

      {leftTab === 'cabling' && (
        <PatchingWorkbench />
      )}

      <EquipmentFormDrawer isOpen={isFormOpen} onClose={() => { setIsFormOpen(false); setEditingItem(null); setFormInitialData(null); }} onSave={handleFormSubmit}
        onCancel={() => { setIsFormOpen(false); setEditingItem(null); setFormInitialData(null); }} initialData={(editingItem || formInitialData) ? { ...(editingItem || formInitialData), rack_id: Number(actualRackId) } as any : null}
        rackUSlots={slots} rackMaxWeight={currentRack?.max_weight_kg || 1000} rackMaxPower={currentRack?.max_power_w || 5000} onValidate={handleValidate}
        catalogSearch={handleCatalogSearchForForm} onCatalogSelect={(e) => handleCatalogSelect(e, freeUBlocks, slots)} onAISuggest={handleAISuggest} isSaving={isSaving} currentWeight={currentWeight} currentPower={currentPower} currentErrors={[]} currentWarnings={[]}
        hasPdu={equipmentList.some(e => e.equipment_type === 'pdu')}
      />

      {tracePortId && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center">
          <VisualCableTrace
            dcCode={actualCode}
            startPortId={tracePortId}
            currentRackId={actualRackId}
            currentRackName={currentRack?.label || currentRack?.name}
            onClose={() => setTracePortId(null)}
          />
        </div>
      )}

      {showRackEditModal && currentRack && (
        <RackEditModal rack={currentRack} datacenterCode={actualCode} onClose={() => setShowRackEditModal(false)}
          onSuccess={() => { queryClient.invalidateQueries({ queryKey: ['rackConfig', actualCode, actualRackId] }); queryClient.invalidateQueries({ queryKey: ['racks', actualCode] }); }}
        />
      )}

      {showReservationModal && (
        <RackReservationModal
          isOpen={showReservationModal}
          onClose={() => setShowReservationModal(false)}
          onSubmit={async (data) => {
            const res = await addReservation(data);
            if (res.success) {
              toast.success(`Зарезервировано U${data.u_start}..${data.u_start + data.u_height - 1} под ${data.tenant}`);
            }
            return res;
          }}
          totalU={slots}
          equipmentList={equipmentList}
          reservations={reservations}
          theme={theme}
        />
      )}
    </div>
  );
}
